import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  HostListener,
} from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

interface AutocompleteOption {
  text: string;
  value: any;
}

@Component({
    selector: 'app-input-pill',
    templateUrl: './input-pill.component.html',
    styleUrls: ['./input-pill.component.scss'],
    standalone: false
})
export class InputPillComponent implements OnInit, OnDestroy {
  public newInput: FormControl;
  @Input() avoidNewInput: boolean;
  @Input() autocompleteOptions: AutocompleteOption[] = [];
  @Input() maxElements;
  @Input() disabled = false;
  @Input() control: FormArray;
  @Input() validators;
  @Input() placeholder = '';
  @Input() tooltip;
  @Input() minLength = 0;
  @Input() translationLabel = '';
  @Input() label = '';
  @Input() autocompleteOptionsOnly = false;
  public showOptions: boolean = false;
  public filteredOptions: AutocompleteOption[] = [];
  public destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private cd: ChangeDetectorRef) {}

  get isDisabled(): boolean {
    return this.disabled || !!this.control?.disabled;
  }

  ngOnInit(): void {
    this.newInput = this?.validators?.length
      ? new FormControl('', this.validators)
      : new FormControl('');
    this.newInput.valueChanges
      .pipe(map(value => this.filterOptions(value)))
      .subscribe(
        filtered =>
          (this.filteredOptions = filtered.sort((a, b) =>
            a.text.localeCompare(b.text)
          ))
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get shouldDisable() {
    const input = this.newInput.value.trim();
    if (
      !input ||
      !this.newInput.valid ||
      this.control
        .getRawValue()
        .some(item => this.getItemText(item) === input) ||
      (this.minLength && input.length < this.minLength) ||
      (this.maxElements &&
        this.maxElements <= this.control.getRawValue().length) ||
      (this.autocompleteOptionsOnly &&
        this.autocompleteOptions?.length &&
        !this.autocompleteOptions.some(option => option.text === input))
    ) {
      return true;
    }
  }

  onFocus() {
    if (this.autocompleteOptions?.length) {
      this.filteredOptions = this.autocompleteOptions.filter(
        option =>
          !this.control
            .getRawValue()
            .some(item => this.getItemText(item) === option.text)
      );
    }
  }

  @HostListener('mouseout', ['$event'])
  onMouseOut(event: MouseEvent) {
    const target = event.relatedTarget as HTMLElement;
    const isInside = target && target.closest('.input-pill-wrapper');
    if (!isInside) {
      this.filteredOptions = [];
    }
  }

  filterOptions(value: string): AutocompleteOption[] {
    if (!value || !this?.autocompleteOptions?.length) return [];
    const filterValue = value.toLowerCase();
    return this.autocompleteOptions.filter(
      option =>
        // option.toLowerCase().includes(filterValue) &&
        option?.text?.toLowerCase &&
        option?.text?.toLowerCase().startsWith(filterValue) &&
        !this.control
          .getRawValue()
          .some(item => this.getItemText(item) === option.text)
    );
  }

  selectOption(option: AutocompleteOption) {
    if (this.isDisabled) return;
    if (
      !option ||
      this.control
        .getRawValue()
        .some(item => this.getItemText(item) === option.text) ||
      (this.maxElements &&
        this.maxElements <= this.control.getRawValue().length)
    ) {
      return;
    }

    this.control.push(new FormControl(option));
    this.newInput.setValue('');
  }

  removeElement(controlelement) {
    if (this.isDisabled) return;

    const elementText = this.getItemText(controlelement);
    const index = this.control.value.findIndex(el =>
      this.getItemText(el) === elementText
    );
    if (index !== -1) {
      this.control.removeAt(index);
      this.cd.detectChanges();
    }
  }

  addInput() {
    if (this.isDisabled || this.avoidNewInput) return;
    const input = this?.newInput.value.trim();
    if (
      !input ||
      !this.newInput.valid ||
      this.control
        .getRawValue()
        .some(item => this.getItemText(item) === input) ||
      (this.minLength && input.length < this.minLength) ||
      (this.maxElements &&
        this.maxElements <= this.control.getRawValue().length) ||
      (this.autocompleteOptionsOnly &&
        this.autocompleteOptions?.length &&
        !this.autocompleteOptions.some(option => option.text === input))
    ) {
      return;
    }

    const newOption: AutocompleteOption = { text: input, value: input };
    this.control.push(new FormControl(newOption));
    this.newInput.setValue('');
  }

  private getItemText(item: any): string {
    // Handle both object format and direct value format
    if (typeof item === 'object' && item !== null) {
      return item.text || item.value || '';
    }
    return item?.toString() || '';
  }
}
